import { readSubmission, SubmissionFormData, type Submission, type SubmissionRequest } from "@/lib/submission";
/**
 * Contributions (v2 Contribution moderation tickets): list, read, submit, and
 * editor decisions (approve / reject / request-revision).
 *
 * Single source for the contribution transport that used to be copy-pasted as
 * raw fetch() across ContributionDetail, CoverContributionDetail, Dashboard,
 * Contribute, and CoverEdit. Each of those sites manually
 * re-read VITE_API_URL, read the CSRF cookie, hand-built headers, parsed res.ok,
 * and re-implemented camelCase/snake_case normalization. All of that lives here
 * now and goes through the configured apiClient.
 *
 * The backend renders responses with CamelCaseJSONRenderer, so reads use
 * camelCase first and fall back to snake_case. ensureCsrfToken() runs before
 * every unsafe verb so a freshly-landed user does not 403 on the first write.
 */
import apiClient, { ensureCsrfToken } from "@/lib/api";
import type { AuthUser } from "@/lib/auth";
import { countyIntentFromApi, type CountyIntent } from "@/lib/contributionDisplay";
import {
  sourceMarkingImageFromContribution,
  type SourceMarkingImage,
} from "@/lib/coverFromImageHandoff";

const CONTRIBUTION_UPLOAD_TIMEOUT_MS = 300000;

/**
 * One contribution row off the wire. Tolerates camelCase (renderer) and
 * snake_case, plus the optional display fields the editor lists read.
 */
export interface ContributionApiItem {
  id: number;
  collection?: number | null;
  status?: string;
  contributor?: number | null;
  contributorId?: number | null;
  contributor_username?: string;
  contributorUsername?: string;
  review_notes?: string | null;
  reviewNotes?: string | null;
  created_at?: string;
  createdAt?: string;
  submitted_data?: unknown;
  submittedData?: unknown;
  display_name?: string;
  displayName?: string;
  updatedAt?: string;
  townDisplay?: string;
  stateDisplay?: string;
  typeDisplay?: string;
  shapeDisplay?: string;
  colorDisplay?: string;
  sizeDisplay?: string;
  imageUrl?: string;
  description?: string;
  isArchived?: boolean;
  archivedAt?: string;
  archivedByUsername?: string;
  archiveReason?: string;
  marking_id?: number | null;
  markingId?: number | null;
  cover_id?: number | null;
  coverId?: number | null;
  // ContributionDetailSerializer exposes the approved marking link as plain
  // "marking" (the FK pk), distinct from the list serializer's "marking_id".
  marking?: number | null;
  // ContributionDetailSerializer resolves a "Create cover from this image"
  // submission's carried-over marking image (Trello T37); list rows omit it.
  source_marking_image?: unknown;
  sourceMarkingImage?: unknown;
  // ContributionDetailSerializer: a pending edit's target as it stands now,
  // in the submit keys (Trello T39); null otherwise and on list rows.
  edit_baseline?: unknown;
  editBaseline?: unknown;
  county_intent?: unknown;
  [k: string]: unknown;
}

/** DRF paginated envelope for GET /contributions/ (count/results survive camelCasing). */
export interface ContributionListResponse {
  count?: number;
  next?: string | null;
  previous?: string | null;
  results?: ContributionApiItem[];
}

/**
 * Normalized contribution for the SPA. Replaces the inline Contribution /
 * ContributionRecord / SubmittedData interfaces that used to live in the page
 * components. `submittedData` is read once at this API boundary into the shared
 * Submission type. Display helpers receive that type.
 */
export interface Contribution {
  id: number;
  collectionId: number | null;
  contributorId: number | null;
  status: string;
  contributorUsername: string;
  reviewNotes: string | null;
  createdAt: string;
  submittedData: Submission;
  unsupportedSubmissionFields?: string[];
  displayName?: string;
  updatedAt?: string;
  townDisplay?: string;
  stateDisplay?: string;
  typeDisplay?: string;
  shapeDisplay?: string;
  colorDisplay?: string;
  sizeDisplay?: string;
  imageUrl?: string;
  description?: string;
  isArchived?: boolean;
  archivedAt?: string;
  archivedByUsername?: string;
  archiveReason?: string;
  markingId: number | null;
  coverId: number | null;
  /**
   * The marking image a "Create cover from this image" submission carries,
   * resolved by the detail endpoint; null when there is none, when it has
   * since moved off the marking, or on list rows (Trello T37).
   */
  sourceMarkingImage: SourceMarkingImage | null;
  /**
   * The edit's target as it stands now, in the same keys as submittedData, for
   * the review page's current-vs-proposed table. Null unless a reviewer is
   * viewing an edit that awaits a decision (Trello T39).
   */
  editBaseline: Submission | null;
  /** The server's verdict on the submission's county, or null (Trello T70). */
  countyIntent: CountyIntent | null;
}

export interface CatalogCodeSuggestion {
  catalogCode: string;
  prefix: string;
  referenceCode: string;
  regionAbbrev: string;
  subjectType: "MARKING" | "COVER";
}

function toNumberOrNull(v: unknown): number | null {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function listImageUrl(item: ContributionApiItem): string {
  const image = item.mainImage ?? item.main_image;
  if (typeof image === "string") return image;
  if (image && typeof image === "object") {
    const row = image as { imageUrl?: unknown; image_url?: unknown };
    const url = row.imageUrl ?? row.image_url;
    if (typeof url === "string") return url;
  }
  return String(item.imageUrl ?? item.image_url ?? "");
}

/** First value that is neither null nor undefined, else null. */
function firstDefined(...vals: unknown[]): unknown {
  for (const v of vals) {
    if (v !== undefined && v !== null) return v;
  }
  return null;
}

export function mapApiItemToContribution(item: ContributionApiItem): Contribution {
  const submittedRaw = item.submitted_data ?? item.submittedData ?? {};
  const parsed = readSubmission(submittedRaw);
  const baselineRaw = firstDefined(item.edit_baseline, item.editBaseline);
  const baseline = baselineRaw == null ? null : readSubmission(baselineRaw);
  return {
    id: item.id,
    collectionId: toNumberOrNull(item.collection),
    contributorId: toNumberOrNull(firstDefined(item.contributor, item.contributorId)),
    status: item.status ?? "pending",
    contributorUsername: String(
      item.contributor_username ?? item.contributorUsername ?? "",
    ),
    reviewNotes: (item.review_notes ?? item.reviewNotes ?? null) as string | null,
    createdAt: String(item.created_at ?? item.createdAt ?? ""),
    submittedData: parsed.data,
    unsupportedSubmissionFields: [...parsed.unsupported, ...(baseline?.unsupported ?? [])],
    displayName: (item.display_name ?? item.displayName) as string | undefined,
    updatedAt: String(item.updated_at ?? item.updatedAt ?? item.created_at ?? item.createdAt ?? ""),
    townDisplay: String(item.town_display || item.townDisplay || parsed.data.town || ""),
    stateDisplay: String(item.state_display || item.stateDisplay || parsed.data.state || ""),
    typeDisplay: String(item.type_display || item.typeDisplay || item.type || parsed.data.type || ""),
    shapeDisplay: String(item.shape_display || item.shapeDisplay || item.shapeName || item.shape || parsed.data.shape || ""),
    colorDisplay: String(item.color_display || item.colorDisplay || item.color || parsed.data.color || ""),
    sizeDisplay: String(item.sizeDisplay || item.size || parsed.data.dimensions || ""),
    imageUrl: listImageUrl(item),
    description: String(item.description || parsed.data.description || ""),
    isArchived: (item.is_archived ?? item.isArchived) === true,
    archivedAt: String(item.archived_at ?? item.archivedAt ?? ""),
    archivedByUsername: String(item.archived_by_username ?? item.archivedByUsername ?? ""),
    archiveReason: String(item.archive_reason ?? item.archiveReason ?? ""),
    // "marking" is what the detail serializer returns; without it the detail
    // page sees a null marking id and the approved-to-record redirect never fires.
    markingId: toNumberOrNull(firstDefined(item.marking_id, item.markingId, item.marking)),
    coverId: toNumberOrNull(firstDefined(item.cover_id, item.coverId)),
    sourceMarkingImage: sourceMarkingImageFromContribution(item),
    editBaseline: baseline?.data ?? null,
    countyIntent: countyIntentFromApi(item.county_intent),
  };
}

export function canReviewContribution(user: AuthUser | null, contribution: Contribution | null): boolean {
  if (!user || !contribution || contribution.status !== "pending") return false;
  if (user.is_superuser || user.role === "administrator") return true;
  return (
    user.role === "editor" && contribution.collectionId != null &&
    (user.assigned_collections ?? []).some((collection) => collection.id === contribution.collectionId)
  );
}

/** Query filters for GET /contributions/. camelCase here maps to snake_case params. */
export interface ContributionListParams {
  /** "archived" is the editor recycle bin -- same scoping as "editor" (#89). */
  mode?: "editor" | "archived";
  status?: string;
  state?: string;
  /**
   * Free text (#109). Matches town, state, type, inscription, the VPHC
   * reference code and the contributor's username, plus the entry number when
   * the text is all digits.
   */
  q?: string;
  town?: string;
  /** Shape NAME as stored in submitted_data ("C - Circle"), not the Shape id. */
  shape?: string;
  color?: string;
  /** ISO yyyy-mm-dd. Both ends inclusive; anything else is a 400. */
  submittedFrom?: string;
  submittedTo?: string;
  /**
   * Where the row came from (#101). "vphc" is the ingest, "human" is a person
   * who used the submission form. Bulk review defaults to "vphc" so a
   * select-all cannot sweep up a real submission -- the queue is live, and a
   * whole-queue operation nearly destroyed eight of them once already.
   */
  source?: "vphc" | "human" | "all";
  page?: number;
  pageSize?: number;
  ordering?: string;
}

export interface ContributionListResult {
  items: Contribution[];
  /** null when the API returns a bare array instead of a paginated envelope. */
  count: number | null;
  next: string | null;
}

/** GET /contributions/ with optional filters. Handles array or paginated responses. */
/**
 * Wire params for the queue, shared by the list and by "select all matching".
 *
 * Both endpoints MUST send the same filters. The backend runs each through the
 * identical filter_queryset(get_queryset()) path, so if these two callers built
 * their query differently, "select all 14 matching" would select a different 14
 * from the ones on screen -- and an editor would approve rows they never saw.
 * Pagination is excluded: the id list is the whole match set, not a page of it.
 */
function buildContributionQuery(
  params?: ContributionListParams,
): Record<string, string | number> {
  const query: Record<string, string | number> = {};
  if (params?.mode) query.mode = params.mode;
  if (params?.status) query.status = params.status;
  if (params?.state) query.state = params.state;
  // Issue #109: these have to reach the API. They used to be applied in the
  // browser to the page it had already fetched, so at 2,440 rows a search
  // silently returned "nothing" unless the target was on the current page.
  if (params?.q?.trim()) query.q = params.q.trim();
  if (params?.town?.trim()) query.town = params.town.trim();
  if (params?.shape && params.shape !== "all") query.shape = params.shape;
  if (params?.color && params.color !== "all") query.color = params.color;
  if (params?.submittedFrom) query.submitted_from = params.submittedFrom;
  if (params?.submittedTo) query.submitted_to = params.submittedTo;
  if (params?.source) query.source = params.source;
  return query;
}

export async function listContributions(
  params?: ContributionListParams,
): Promise<ContributionListResult> {
  const query = buildContributionQuery(params);
  if (params?.page != null) query.page = params.page;
  if (params?.pageSize != null) query.page_size = params.pageSize;
  if (params?.ordering) query.ordering = params.ordering;

  const res = await apiClient.get("/contributions/", { params: query });
  const data = res.data;
  const rawItems: ContributionApiItem[] = Array.isArray(data)
    ? (data as ContributionApiItem[])
    : Array.isArray((data as ContributionListResponse)?.results)
      ? ((data as ContributionListResponse).results as ContributionApiItem[])
      : [];
  const count = Array.isArray(data)
    ? data.length
    : typeof (data as ContributionListResponse)?.count === "number"
      ? ((data as ContributionListResponse).count as number)
      : null;
  const next = !Array.isArray(data) && typeof (data as ContributionListResponse)?.next === "string"
    ? (data as ContributionListResponse).next as string
    : null;
  return { items: rawItems.map(mapApiItemToContribution), count, next };
}

/** Load every submission owned by the signed-in user for My Submissions. */
export async function listMyContributions(): Promise<{
  items: Contribution[];
  incomplete: boolean;
}> {
  const pageSize = 100;
  const all: Contribution[] = [];
  for (let page = 1; ; page += 1) {
    let result: ContributionListResult;
    try {
      result = await listContributions({ page, pageSize });
    } catch (error) {
      if (page === 1) throw error;
      return { items: all, incomplete: true };
    }
    all.push(...result.items);
    if (!result.next) {
      return { items: all, incomplete: result.count !== null && all.length < result.count };
    }
  }
}

/** GET /contributions/{id}/ -- normalized single contribution. Errors propagate. */
export async function getContribution(id: number): Promise<Contribution> {
  const res = await apiClient.get(`/contributions/${id}/`);
  return mapApiItemToContribution(res.data as ContributionApiItem);
}

/** Minimal write result; `raw` is the untouched response for any field not unified here. */
export interface ContributionWriteResult {
  contributionId: number | null;
  markingId: number | null;
  coverId: number | null;
  raw: Record<string, unknown>;
}

function mapWriteResult(raw: unknown): ContributionWriteResult {
  const o = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  return {
    contributionId: toNumberOrNull(
      firstDefined(o.contributionId, o.contribution_id, o.id),
    ),
    markingId: toNumberOrNull(firstDefined(o.marking_id, o.markingId)),
    coverId: toNumberOrNull(firstDefined(o.cover_id, o.coverId)),
    raw: o,
  };
}

/**
 * POST /contributions/ -- submit a contribution (new, draft, edit, or abandon).
 *
 * The caller builds the body. Pass a FormData when uploading images (apiClient
 * strips Content-Type so the browser sets the multipart boundary) or a plain
 * object for JSON submissions (axios sets application/json). The 30-field body
 * builders stay in the page components since they are tightly coupled to form
 * state; this function owns only CSRF + transport + response normalization.
 */
export async function createContribution(
  body: SubmissionFormData | SubmissionRequest,
): Promise<ContributionWriteResult> {
  await ensureCsrfToken();
  const isMultipart = typeof FormData !== "undefined" && body instanceof FormData;
  const res = await apiClient.post(
    "/contributions/",
    body,
    isMultipart ? { timeout: CONTRIBUTION_UPLOAD_TIMEOUT_MS } : undefined,
  );
  return mapWriteResult(res.data);
}

/**
 * Discard a cover Contribution draft after its cover was submitted via the
 * catalog API. Thin wrapper over createContribution with the abandon flags.
 */
export async function abandonCoverContributionDraft(contributionId: number): Promise<void> {
  const form = new SubmissionFormData();
  form.append("submission_kind", "cover");
  form.append("abandon_draft", "true");
  form.append("edit_contribution_id", String(contributionId));
  await createContribution(form);
}

export type ContributionAction = "approve" | "reject" | "revision";

export interface DecideOptions {
  /** Saved as review_notes; sent only when non-empty. */
  reviewNotes?: string;
  /** Approve only; sent only when provided. */
  estimatedValue?: number;
  /** Approve only; editor-owned catalog code. Empty means regenerate. */
  catalogCode?: string | null;
}

export interface DecideResult {
  markingId: number | null;
  coverId: number | null;
  /** Trello T37: the Cover submission approval created from a Marking that carried one. */
  pendingCoverContributionId: number | null;
  raw: Record<string, unknown>;
}

/**
 * GET /contributions/ids/ -- every id matching the current filters (#101).
 *
 * This is what "select all matching" means. It deliberately does NOT reuse the
 * fetched page: #109's lesson is that anything derived client-side from one
 * page lies at scale, and here that lie would approve the wrong rows.
 */
export async function listContributionIds(
  params?: ContributionListParams,
): Promise<{ ids: number[]; count: number }> {
  const res = await apiClient.get("/contributions/ids/", {
    params: buildContributionQuery(params),
  });
  const data = res.data as { ids?: unknown; count?: unknown };
  const ids = Array.isArray(data.ids)
    ? data.ids.filter((v): v is number => typeof v === "number")
    : [];
  return { ids, count: typeof data.count === "number" ? data.count : ids.length };
}

export interface BulkReviewFailure {
  id: number;
  reason: string;
}

export interface BulkReviewResult {
  succeeded: number[];
  failed: BulkReviewFailure[];
}

/**
 * Rows per request. The server caps at 50; this is the client's working size.
 *
 * Measured on a full-size queue (2,443 pending): one approval is ~174 ms mean,
 * ~274 ms worst, so 25 rows is ~4.4 s and the whole queue is ~7 minutes. Small
 * enough to stay far clear of any proxy timeout, large enough that draining the
 * queue is ~98 requests rather than 2,443.
 */
export const BULK_REVIEW_BATCH_SIZE = 25;

/**
 * POST /contributions/bulk-{approve,reject}/ over `ids`, in batches.
 *
 * Sequential, not parallel: every row in a batch mints catalog codes off the
 * same prefix, and concurrent requests would race for the same serial. The
 * server retries that race, but there is no reason to manufacture it -- and
 * sequential batches give an honest progress bar.
 *
 * Failures never abort the run. Each row commits independently server-side, so
 * a bad row is reported and the rest still land (#101 AC-4); the caller gets
 * every failure at the end and can retry just those ids.
 */
export async function bulkReviewContributions(
  action: "approve" | "reject",
  ids: number[],
  options?: {
    reviewNotes?: string;
    onProgress?: (done: number, total: number) => void;
    signal?: AbortSignal;
  },
): Promise<BulkReviewResult> {
  const path = action === "approve" ? "bulk-approve" : "bulk-reject";
  const key = action === "approve" ? "approved" : "rejected";
  const succeeded: number[] = [];
  const failed: BulkReviewFailure[] = [];

  for (let i = 0; i < ids.length; i += BULK_REVIEW_BATCH_SIZE) {
    if (options?.signal?.aborted) break;
    const slice = ids.slice(i, i + BULK_REVIEW_BATCH_SIZE);
    try {
      const res = await apiClient.post(
        `/contributions/${path}/`,
        { ids: slice, review_notes: options?.reviewNotes ?? "" },
        { signal: options?.signal },
      );
      const data = res.data as Record<string, unknown>;
      const ok = Array.isArray(data[key]) ? (data[key] as number[]) : [];
      succeeded.push(...ok);
      const bad = Array.isArray(data.failed) ? (data.failed as BulkReviewFailure[]) : [];
      failed.push(...bad);
    } catch (err) {
      // A transport-level failure loses the whole slice, not the run. Report
      // every id in it so the editor can retry them rather than being told
      // "1,150 of 2,443" with no account of the missing ones.
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
        (err as Error)?.message ??
        "Request failed.";
      for (const id of slice) failed.push({ id, reason: detail });
    }
    options?.onProgress?.(Math.min(i + slice.length, ids.length), ids.length);
  }

  return { succeeded, failed };
}

/**
 * POST /contributions/{id}/{approve|reject|request-revision}/.
 * "revision" maps to the "request-revision" path.
 */
export async function decideContribution(
  id: number,
  action: ContributionAction,
  opts?: DecideOptions,
): Promise<DecideResult> {
  await ensureCsrfToken();
  const actionPath =
    action === "approve" ? "approve" : action === "reject" ? "reject" : "request-revision";
  const body: Record<string, unknown> = {};
  const notes = opts?.reviewNotes?.trim();
  if (notes) body.review_notes = notes;
  if (opts?.estimatedValue != null) body.estimated_value = opts.estimatedValue;
  if (opts && "catalogCode" in opts) body.catalog_code = opts.catalogCode ?? "";

  const res = await apiClient.post(`/contributions/${id}/${actionPath}/`, body);
  const o = res.data && typeof res.data === "object" ? (res.data as Record<string, unknown>) : {};
  return {
    markingId: toNumberOrNull(firstDefined(o.marking_id, o.markingId)),
    coverId: toNumberOrNull(firstDefined(o.cover_id, o.coverId)),
    pendingCoverContributionId: toNumberOrNull(
      firstDefined(o.pendingCoverContributionId, o.pending_cover_contribution_id),
    ),
    raw: o,
  };
}

function mapCatalogCodeSuggestion(raw: unknown): CatalogCodeSuggestion {
  const o = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  return {
    catalogCode: String(o.catalog_code ?? o.catalogCode ?? ""),
    prefix: String(o.prefix ?? ""),
    referenceCode: String(o.reference_code ?? o.referenceCode ?? ""),
    regionAbbrev: String(o.region_abbrev ?? o.regionAbbrev ?? ""),
    subjectType:
      String(o.subject_type ?? o.subjectType ?? "MARKING").toUpperCase() === "COVER"
        ? "COVER"
        : "MARKING",
  };
}

export async function getContributionCatalogCodeSuggestion(
  contributionId: number,
  opts?: { force?: boolean },
): Promise<CatalogCodeSuggestion> {
  await ensureCsrfToken();
  const res = await apiClient.post(
    `/contributions/${contributionId}/catalog-code-suggestion/`,
    opts?.force ? { force: true } : {},
  );
  return mapCatalogCodeSuggestion(res.data);
}

export async function getDirectCatalogCodeSuggestion(payload: {
  subjectType: "MARKING" | "COVER";
  state?: string;
  regionId?: number | null;
  markingId?: number | null;
  referenceWorkId?: number | null;
  referenceWorkIds?: number[];
  excludeId?: number | null;
}): Promise<CatalogCodeSuggestion> {
  await ensureCsrfToken();
  const res = await apiClient.post("/catalog-code-suggestions/", {
    subject_type: payload.subjectType,
    state: payload.state,
    region_id: payload.regionId ?? undefined,
    marking_id: payload.markingId ?? undefined,
    reference_work_id: payload.referenceWorkId ?? undefined,
    reference_work_ids: payload.referenceWorkIds,
    exclude_id: payload.excludeId ?? undefined,
  });
  return mapCatalogCodeSuggestion(res.data);
}

// DELETE /contributions/{id}/  -- hard-deletes (withdraws) an unapproved
// contribution; backend enforces IsOwnDeletableContribution (status must NOT be
// "approved" and requester must be the owner or a superuser). No response body.
export async function deleteOwnContribution(contributionId: number): Promise<void> {
  await ensureCsrfToken();
  await apiClient.delete(`/contributions/${contributionId}/`);
}

/** Result shape shared by the archive/restore actions; mirrors removeMarking. */
type ArchiveResult = { ok: true } | { ok: false; message: string };

function archiveError(err: unknown, fallback: string): ArchiveResult {
  const ax = err as { response?: { data?: { detail?: string } } };
  const detail = ax.response?.data?.detail;
  return { ok: false, message: typeof detail === "string" ? detail : fallback };
}

/**
 * Editor: POST /contributions/{id}/archive/ -- clear a reviewed entry off the
 * review dashboard (Issue #89). Soft: the contribution is untouched and stays
 * visible to its contributor. The backend rejects a pending contribution.
 */
export async function archiveContribution(
  contributionId: number,
  reason?: string,
): Promise<ArchiveResult> {
  await ensureCsrfToken();
  try {
    await apiClient.post(`/contributions/${contributionId}/archive/`, {
      reason: reason ?? "",
    });
    return { ok: true };
  } catch (err: unknown) {
    return archiveError(err, "Could not archive this entry.");
  }
}

/** Editor: POST /contributions/{id}/restore/ -- put an archived entry back in the queue. */
export async function restoreContribution(
  contributionId: number,
): Promise<ArchiveResult> {
  await ensureCsrfToken();
  try {
    await apiClient.post(`/contributions/${contributionId}/restore/`, {});
    return { ok: true };
  } catch (err: unknown) {
    return archiveError(err, "Could not restore this entry.");
  }
}
